import React from 'react';
import {ModalBody, ModalFooter} from 'reactstrap';
import {toast} from 'react-toastify';
import {APIClient} from "../../../utils/APIClient";
import {isOk} from "../../../utils/apiResponse";
import {API_URL} from "../../../settings/local_settings";
import {Spinner} from "../../Spinner";
import {DAILY_LIMIT_TYPE_DAILY_AD_ACCOUNT_COST_AND_BUDGET} from "../../../daily_limits";
import AdAccountReadOnlyInput from "../../elements/AdAccountReadOnlyInput";
import {ClientAdAccountFormBaseClass} from "./ClientAdAccountFormBase";
import {Ga4AttributionSection} from "./Ga4AttributionSection";
import {isKnownChannel} from "../../ga4_attribution/attributionModel";
import {isForbidden, saveAccountRule} from "../../ga4_attribution/ga4AttributionApi";
import { withTranslation } from 'react-i18next';


const RULE_SAVED = 'saved';
const RULE_UNCHANGED = 'unchanged';
const RULE_FAILED = 'failed';

class EditClientAdAccountFormComponent extends ClientAdAccountFormBaseClass {
    constructor(props) {
        super(props);

        this.state = {
            ...this.state,
            updateClientAdAccount: false,
            daily_limit_type: this.props.clientAdAccount.daily_limit_type ? this.props.clientAdAccount.daily_limit_type : DAILY_LIMIT_TYPE_DAILY_AD_ACCOUNT_COST_AND_BUDGET,
            // Sin límite guardado, la moneda arranca en la de la CUENTA y no en ARS fijo. Con los
            // tipos por consumo deja de ser cosmético: el gasto que se compara viene en la moneda de
            // la cuenta, así que elegir otra por descuido hace saltar la alarma en otro umbral.
            daily_limit_currency: this.props.clientAdAccount.daily_limit_currency
                || this.props.clientAdAccount.currency_code || "ARS",
            daily_limit_value: this.props.clientAdAccount.daily_limit_value,
            dailyLimitEnabled: this.isDailyLimitEnabledOnClientAdAccount(this.props.clientAdAccount),
            // El cambio pendiente de la regla de cruce con GA4 (el body del PUT), o `null` si no hay.
            // Lo avisa `Ga4AttributionSection`; se guarda junto con el límite, en el mismo «Guardar».
            ga4AttributionDraft: null,
            // `key` de la sección: subirla la remonta, y al remontarse vuelve a leer la regla guardada.
            ga4AttributionVersion: 0,
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.footerButton = this.footerButton.bind(this);
        this.handleGa4AttributionDraftChange = this.handleGa4AttributionDraftChange.bind(this);
    }

    handleGa4AttributionDraftChange(draft) {
        this.setState({ga4AttributionDraft: draft});
    }

    currentChannel() {
        return this.props.clientAdAccount.channel;
    }

    savedDailyLimitType() {
        return this.props.clientAdAccount.daily_limit_type;
    }

    isDailyLimitEnabledOnClientAdAccount(clientAdAccount) {
        return clientAdAccount.daily_limit_currency !== undefined && clientAdAccount.daily_limit_currency !== null && clientAdAccount.daily_limit_value !== undefined && clientAdAccount.daily_limit_value !== null;
    }

    /**
     * ¿El límite diario del formulario difiere del guardado? Si no, el POST no sale: el formulario
     * arranca con un TIPO por defecto aunque la cuenta no tenga límite, así que reenviarlo «igual»
     * escribía ese tipo en la cuenta (y una entrada en la Bitácora) cada vez que alguien guardaba sólo
     * la regla de cruce con GA4 (QA-reglafe-11). Con el límite apagado en los dos lados, el tipo no
     * significa nada y no cuenta como cambio.
     */
    dailyLimitChanged() {
        const saved = this.props.clientAdAccount;
        const wasEnabled = this.isDailyLimitEnabledOnClientAdAccount(saved);
        if (!this.state.dailyLimitEnabled) return wasEnabled;
        if (!wasEnabled) return true;
        return this.state.daily_limit_type !== saved.daily_limit_type
            || this.state.daily_limit_currency !== saved.daily_limit_currency
            || Number(this.state.daily_limit_value) !== Number(saved.daily_limit_value);
    }

    /**
     * Un solo «Guardar» para las dos cosas del modal. PRIMERO la regla de cruce con GA4 (si cambió),
     * después el límite diario (si cambió): si la regla falla, el límite no se manda y el modal queda
     * abierto con lo que el usuario cargó, para reintentar. Al revés, un límite guardado con la regla
     * caída cerraría el modal como si todo hubiera salido bien.
     *
     * Si la regla se guardó y el límite no, la regla YA está en el backend: se dice, y la sección se
     * remonta para partir de lo guardado. Si no, seguiría comparando contra lo que leyó al abrir, y
     * volver a la opción de antes no daría borrador: el reintento cerraría con la regla nueva puesta
     * sin que nadie lo note (review C-regla-frontend-02).
     */
    handleSubmit(event) {
        event.preventDefault();

        this.setState({
            updateClientAdAccount: true
        });

        this.saveGa4AttributionRule().then((ruleResult) => {
            if (ruleResult === RULE_FAILED) {
                this.setState({updateClientAdAccount: false});
                return;
            }
            if (!this.dailyLimitChanged()) {
                this.setState({updateClientAdAccount: false});
                this.props.afterSuccessfulEdited(this.props.clientAdAccount.id);
                return;
            }
            new APIClient(this.submitUrl()).call('POST', this._getDataToPost()).then((data) => {
                this.setState({
                    updateClientAdAccount: false
                });

                // `!isOk` y no `status === 'ERROR'`: un error tipado o un fallo de red no valen
                // 'ERROR', y se iban sin aviso con el spinner apagado.
                if (!isOk(data)) {
                    if (ruleResult === RULE_SAVED) this.adoptSavedGa4AttributionRule();
                    alert(data.error_message);
                    return;
                }
                this.props.afterSuccessfulEdited(data.data.agency_client_ad_account_id)
            });
        });
    }

    /** `RULE_SAVED`, `RULE_UNCHANGED` (no había borrador) o `RULE_FAILED` (ya avisado). */
    saveGa4AttributionRule() {
        const draft = this.state.ga4AttributionDraft;
        if (!draft) return Promise.resolve(RULE_UNCHANGED);
        const {t} = this.props;
        return saveAccountRule(this.props.clientAdAccount.id, draft).then((response) => {
            if (isOk(response)) return RULE_SAVED;
            toast.error(t(isForbidden(response) ? 'ga4_attribution.agency.forbidden' : 'ga4_attribution.agency.save_error'));
            return RULE_FAILED;
        });
    }

    adoptSavedGa4AttributionRule() {
        toast.success(this.props.t('ga4_attribution.agency.saved_ok'));
        this.setState((prev) => ({ga4AttributionDraft: null, ga4AttributionVersion: prev.ga4AttributionVersion + 1}));
    }

    footerButton() {
        if (this.state.updateClientAdAccount) {
            return <Spinner color="positive small"/>
        } else {
            const { t } = this.props;
            return <input type="submit" value={t('client.save_button')} className="btn -submit"/>
        }
    }

    render() {
        return (
            <form onSubmit={this.handleSubmit}>
                <ModalBody>
                    <div className="modal-section">
                        <div className="form-group">
                            {this.renderClientAndAccountRow()}
                            {this.renderRadioButtonOptions()}
                        </div>
                        {isKnownChannel(this.props.clientAdAccount.channel) && (
                            <Ga4AttributionSection key={this.state.ga4AttributionVersion}
                                                   clientAdAccountId={this.props.clientAdAccount.id}
                                                   channel={this.props.clientAdAccount.channel}
                                                   disabled={this.state.updateClientAdAccount}
                                                   onDraftChange={this.handleGa4AttributionDraftChange}/>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    {this.footerButton()}
                </ModalFooter>
            </form>
        )
    }

    renderClientAndAccountRow() {
        const { t } = this.props;
        return <div className="row">
            <div className="col-md-6">
                <label>{t('edit_client_ad_account_form.client')}</label>
                <input name="client" type="text" value={this.props.clientName} readOnly="readonly"
                       className="form-control pl-3"/>
            </div>
            <div className="col-md-6">
                <label>{t('edit_client_ad_account_form.account')}</label>
                <AdAccountReadOnlyInput channel={this.props.clientAdAccount.channel}
                                        adAccountName={this.props.clientAdAccount.name}
                                        adAccountId={this.props.clientAdAccount.account_id}/>
            </div>
        </div>;
    }



    submitUrl() {
        return API_URL + 'client/ad_account/' + this.props.clientAdAccount.id + '/edit';
    }
}

export const EditClientAdAccountForm = withTranslation()(EditClientAdAccountFormComponent);
